import { useState, useEffect } from 'react';
import {
    Button,
    CardActions,
    CardContent,
    CircularProgress,
    Typography,
    TextField,
    Box,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { AuthPageLayout, LogoHeader } from '../login/components';
import { supabase } from '../providers/supabase/supabase';
import {
    cleanupUnverifiedFactors,
    enrollTotp,
    getVerifiedFactors,
    verifyTotp,
} from '../providers/supabase/mfa';

type EnrollmentData = {
    factorId: string;
    qrCode: string;
    secret: string;
};

export const MfaEnrollPage = () => {
    const [phase, setPhase] = useState<'setup' | 'verify'>('setup');
    const [enrollment, setEnrollment] = useState<EnrollmentData | null>(null);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [error, setError] = useState('');
    const notify = useNotify();
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                // If user already has verified factors, go to dashboard
                const factors = await getVerifiedFactors();
                if (factors.length > 0) {
                    if (!cancelled) navigate('/', { replace: true });
                    return;
                }

                // Clean up any orphaned unverified factors
                await cleanupUnverifiedFactors();

                if (cancelled) return;

                // Start fresh enrollment
                const data = await enrollTotp();
                if (!cancelled) {
                    setEnrollment({
                        factorId: data.id,
                        qrCode: (data as any).totp.qr_code,
                        secret: (data as any).totp.secret,
                    });
                }
            } catch (err: any) {
                if (!cancelled) {
                    console.error('MFA enrollment init failed:', err);
                    setError(
                        'Failed to initialize MFA setup. Please try again.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setInitLoading(false);
                }
            }
        };

        init();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleVerify = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!code || !enrollment) return;

        setLoading(true);
        setError('');

        try {
            await verifyTotp(enrollment.factorId, code);
            notify('Two-factor authentication enabled!', {
                type: 'success',
            });
            navigate('/', { replace: true });
        } catch (err: any) {
            console.error('MFA enrollment verify failed:', err);
            setError('Invalid code. Please try again.');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleCopySecret = () => {
        if (enrollment?.secret) {
            navigator.clipboard.writeText(enrollment.secret);
            notify('Secret copied to clipboard', { type: 'info' });
        }
    };

    const handleSignOut = async () => {
        // Clean up the unverified factor before signing out
        if (enrollment?.factorId) {
            try {
                await supabase.auth.mfa.unenroll({
                    factorId: enrollment.factorId,
                });
            } catch {
                // Ignore — we're signing out anyway
            }
        }
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
    };

    if (initLoading) {
        return (
            <AuthPageLayout centered maxWidth={450}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 4,
                    }}
                >
                    <CircularProgress />
                </Box>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout maxWidth={450}>
            <Title title="Set Up Two-Factor Authentication" />
            <LogoHeader />

            {phase === 'setup' && (
                <>
                    <CardContent sx={{ px: 3 }}>
                        <Typography
                            variant="h6"
                            component="h1"
                            gutterBottom
                            align="center"
                            sx={{ mb: 1 }}
                        >
                            Set Up Two-Factor Authentication
                        </Typography>
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 3, color: 'text.secondary' }}
                        >
                            Your organization requires two-factor
                            authentication. Scan the QR code below with your
                            authenticator app (Google Authenticator, Authy,
                            1Password, etc.)
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {enrollment && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <img
                                        src={enrollment.qrCode}
                                        alt="MFA QR Code"
                                        style={{
                                            width: 200,
                                            height: 200,
                                        }}
                                    />
                                </Box>

                                <Typography
                                    variant="caption"
                                    align="center"
                                    display="block"
                                    sx={{
                                        mb: 1,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Can't scan? Enter this code manually:
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mb: 2,
                                        p: 1.5,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontWeight: 600,
                                            letterSpacing: '0.1em',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {enrollment.secret}
                                    </Typography>
                                    <Tooltip title="Copy secret">
                                        <IconButton
                                            size="small"
                                            onClick={handleCopySecret}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </>
                        )}
                    </CardContent>

                    <CardActions
                        sx={{
                            flexDirection: 'column',
                            gap: 1,
                            px: 2,
                            pb: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={!enrollment}
                            onClick={() => setPhase('verify')}
                        >
                            Next
                        </Button>
                        <Button
                            size="small"
                            onClick={handleSignOut}
                            sx={{
                                mt: 1,
                                color: 'text.secondary',
                                textTransform: 'none',
                            }}
                        >
                            Sign out
                        </Button>
                    </CardActions>
                </>
            )}

            {phase === 'verify' && (
                <form onSubmit={handleVerify}>
                    <CardContent sx={{ px: 3 }}>
                        <Typography
                            variant="h6"
                            component="h1"
                            gutterBottom
                            align="center"
                            sx={{ mb: 1 }}
                        >
                            Verify Setup
                        </Typography>
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 3, color: 'text.secondary' }}
                        >
                            Enter the 6-digit code from your authenticator app
                            to confirm setup.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Verification Code"
                            value={code}
                            onChange={e => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val.length <= 6) setCode(val);
                            }}
                            inputProps={{
                                maxLength: 6,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.5rem',
                                    letterSpacing: '0.5rem',
                                },
                            }}
                            autoFocus
                            autoComplete="one-time-code"
                            disabled={loading}
                            variant="outlined"
                            sx={{ mb: 2 }}
                        />
                    </CardContent>

                    <CardActions
                        sx={{
                            flexDirection: 'column',
                            gap: 1,
                            px: 2,
                            pb: 2,
                        }}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || code.length !== 6}
                            fullWidth
                        >
                            {loading ? (
                                <CircularProgress
                                    size={19}
                                    thickness={3}
                                    sx={{ margin: 0.3 }}
                                />
                            ) : (
                                'Verify & Complete'
                            )}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setPhase('setup');
                                setCode('');
                                setError('');
                            }}
                            disabled={loading}
                            sx={{
                                mt: 1,
                                color: 'text.secondary',
                                textTransform: 'none',
                            }}
                        >
                            Back
                        </Button>
                    </CardActions>
                </form>
            )}
        </AuthPageLayout>
    );
};

MfaEnrollPage.path = '/mfa-enroll';
